import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Pentool from 'components/pages/chart/Pentool';
import ChartBottomBtn from 'components/pages/chart/ChartBottomBtn';
import ConfirmChartRegister from 'pages/modal/ConfirmChartRegister';
import { ExtraIntraOralExaminationChartFormDefaultValues } from 'constants/chart/extraIntraOralExaminationChart';
import { TExtraIntraOralExaminationChartFormValues } from 'types/chart/extraIntraOralExaminationChart';
import { TPracticeChartInfo } from 'types/apis/chart/studentPracticeChart';
import { TEvaluationChartInfo } from 'types/apis/chart/studentEvaluationChart';
import { TTool } from 'types/apis/chart/studentPracticeChart';
import { TLine } from 'types/chart/pentool';
import useModal from 'hooks/useModal';
import { TChartInfoResponse } from 'hooks/chart/useGetChartInfo';
import useChartRegister from 'hooks/chart/useChartRegister';
import Konva from 'konva';
import ExtraIntraOralExaminationChart from '../ExtraIntraOralExaminationChart';

type TProps = {
  query: URLSearchParams;
  chartType: number;
  category: string;
  id: number;
  isProfessor: boolean;
  tool: TTool;
  setTool: (tool: TTool) => void;
  strokeActive: boolean;
  setStrokeActive: (active: boolean) => void;
  strokeWidth: number;
  setStrokeWidth: (strokeWidth: number) => void;
  strokeColor: string;
  setStrokeColor: (strokeColor: string) => void;
  lines: TLine[];
  setLines: (lines: TLine[]) => void;
  chartInfoResponse: TChartInfoResponse | null;
  activeTabIndex: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isRegister: boolean;
  isEdit: boolean;
  setSubmitEndDate: (submitEndDate: string) => void;
  isReadOnly: boolean;
  checkSubmitEndDate: (submitEndDate: string) => void;
  handleSuccessGetChartInfo: (data: TChartInfoResponse) => void;
  setStageImage: (ref: any) => void;
};

const ExtraIntraOralExaminationChartRegister = ({
  query,
  chartType,
  category,
  id,
  isProfessor,
  tool,
  setTool,
  strokeActive,
  setStrokeActive,
  strokeWidth,
  setStrokeWidth,
  strokeColor,
  setStrokeColor,
  lines,
  setLines,
  chartInfoResponse,
  activeTabIndex,
  isLoading,
  setIsLoading,
  isRegister,
  isEdit,
  setSubmitEndDate,
  isReadOnly,
  checkSubmitEndDate,
  handleSuccessGetChartInfo,
  setStageImage,
}: TProps) => {
  const { openModal } = useModal();

  const { chartRegister } = useChartRegister();

  const methods = useForm<TExtraIntraOralExaminationChartFormValues>({
    defaultValues: ExtraIntraOralExaminationChartFormDefaultValues,
  });
  const { handleSubmit, setValue, watch, reset } = methods;
  const formValues = watch();
  const formValuesRef = useRef(formValues);
  const prevFormValuesRef =
    useRef<TExtraIntraOralExaminationChartFormValues | null>(null);

  const stageRef = useRef<any>();
  const penLayerRef = useRef<any>();

  useEffect(() => {
    setValue(
      'OralDrawing' as keyof TExtraIntraOralExaminationChartFormValues,
      stageRef.current?.toJSON() as TExtraIntraOralExaminationChartFormValues['OralDrawing']
    );
    setStageImage(stageRef);
  }, [lines]);

  // 실습용 - 자동 저장 (20초 간격)
  useEffect(() => {
    if (isLoading) return;
    prevFormValuesRef.current = formValues;
  }, [isLoading]);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (!isLoading && isRegister && category === 'evaluation') {
      intervalId = setInterval(() => {
        if (isReadOnly) return;
        // 초기 상태 자동 저장 방지
        if (
          (chartInfoResponse?.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ]?.chartData === null &&
          JSON.stringify(ExtraIntraOralExaminationChartFormDefaultValues) ===
            JSON.stringify(formValuesRef.current)
        )
          return;
        // 이전 값과 비교하여 변경된 경우만 자동 저장 요청
        if (
          JSON.stringify(formValuesRef.current) !==
          JSON.stringify(prevFormValuesRef.current)
        ) {
          onValid(true, formValuesRef.current);
        }
      }, 20000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRegister, isLoading, category, isReadOnly, chartInfoResponse]);

  const setChartInfo = (
    chartInfo: TPracticeChartInfo | TEvaluationChartInfo
  ) => {
    const chartData: TExtraIntraOralExaminationChartFormValues | null =
      (chartInfo?.chartData as TExtraIntraOralExaminationChartFormValues | null) ||
      null;

    if (category !== 'practice') {
      const submitEndDate =
        (chartInfo as TEvaluationChartInfo)?.submitEndDate || '';
      setSubmitEndDate(submitEndDate);
      checkSubmitEndDate(submitEndDate);
    }
    if (!chartData) {
      reset();
      setIsLoading(false);
      return;
    }
    Object.keys(chartData).forEach((key) => {
      setValue(
        key as keyof TExtraIntraOralExaminationChartFormValues,
        chartData[key as keyof TExtraIntraOralExaminationChartFormValues]
      );
    });

    if (!chartData.OralDrawing) {
      setIsLoading(false);
      return;
    }

    const drawingData = JSON.parse(chartData.OralDrawing as string);

    // 데이터에서 라인을 하나라도 가지고 있는 레이어가 있다면 추출
    const includeLineLayers = drawingData.children.filter((layer: any) => {
      return layer.children.some(
        (layerChild: any) => layerChild.className === 'Line'
      );
    });

    const newLines: TLine[] = [];

    includeLineLayers.forEach((lineLayer: any) => {
      lineLayer.children.forEach((lineLayerChild: any) => {
        if (lineLayerChild.className !== 'Line') {
          return;
        }
        // 각 라인으로 새로운 라인 만들기
        const newLine = new Konva.Line(lineLayerChild);

        // 라인의 attrs 에서 points 만 추출
        newLines.push({
          points: newLine.attrs.points,
          strokeColor: newLine.attrs.stroke,
          strokeWidth: newLine.attrs.strokeWidth,
          tool: !newLine.attrs.globalCompositeOperation ? 'pen' : 'eraser',
        });
      });
    });
    // 라인의 attrs 에서 얻은 points 로 state 변경하기
    setLines(newLines);
    setIsLoading(false);
  };

  useEffect(() => {
    if (chartInfoResponse) {
      handleSuccessGetChartInfo(chartInfoResponse);
    }
  }, []);

  // 차트 조회
  useEffect(() => {
    if (!chartInfoResponse) return;
    const chartInfo =
      category === 'practice'
        ? (chartInfoResponse.chartInfo as TPracticeChartInfo)
        : (chartInfoResponse.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ];
    setIsLoading(true);
    setChartInfo(chartInfo);
  }, [activeTabIndex, chartInfoResponse]);

  // 차트 등록
  const onValid = (
    isAutoSave: boolean,
    data: TExtraIntraOralExaminationChartFormValues
  ) => {
    const formData = new FormData();

    formData.append(
      'request',
      new Blob(
        [
          JSON.stringify({
            chartType,
            chartData: data,
          }),
        ],
        {
          type: 'application/json',
        }
      )
    );
    // 학생 차트 등록
    chartRegister({
      category,
      id: id,
      data: formData,
      isEdit: isEdit,
      isAutoSave,
      patientId: query.get('patientId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
      onLoading: setIsLoading,
      onConfirm: () => {
        openModal({
          element: (
            <ConfirmChartRegister
              category={category}
              chartType={chartType}
              id={id}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              chartName={chartInfoResponse?.chartName}
              data={formData}
            />
          ),
        });
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((data) => onValid(false, data))}>
          {!isProfessor && isRegister && (
            <Pentool
              setTool={setTool}
              strokeActive={strokeActive}
              setStrokeActive={setStrokeActive}
              setStrokeWidth={setStrokeWidth}
              setStrokeColor={setStrokeColor}
              isRegister={
                (!isProfessor && isRegister && !isReadOnly) ||
                (isProfessor && !isRegister && isEdit)
              }
              feedbackLines={lines}
              setFeedbackLines={setLines}
            />
          )}
          <ExtraIntraOralExaminationChart
            tool={tool}
            strokeWidth={strokeWidth}
            setStrokeWidth={setStrokeWidth}
            strokeColor={strokeColor}
            strokeActive={strokeActive}
            lines={lines}
            setLines={setLines}
            stageRef={stageRef}
            penLayerRef={penLayerRef}
            isProfessor={isProfessor}
            isRegister={isRegister}
            isReadOnly={isReadOnly}
          />

          {(Number(query.get('tab')) === 0 ||
            Number(query.get('tab')) === 2) && (
            <ChartBottomBtn
              category={category}
              id={id}
              courseId={query.get('courseId') || undefined}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              studentUserId={query.get('studentUserId') || undefined}
              isRegister={isRegister}
              isEdit={isEdit}
              isReadOnly={isReadOnly}
              isChart={true}
              chartName={chartInfoResponse?.chartName}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default ExtraIntraOralExaminationChartRegister;
